import {
  type FilterToken,
  isFilterToken,
  type ParseFilterOptions,
  parseFilterTree,
  valuesOf,
} from './tree.ts';

/**
 * Дати мови умов (filter-language.md, розд. 2, 3; FLT-13): токени днів, діапазонів і моменту та
 * межі дня в часовому поясі. Одна реалізація для компілятора DAL (межі йдуть параметрами SQL) і
 * оцінювача в браузері, тож «SQL = оцінювач» тримається за побудовою, а не збігом tzdata з ICU.
 *
 * - День — календарна дата `YYYY-MM-DD` у поясі годинника (`FilterClock`).
 * - Межі дня для поля «дата-час» — момент початку дня в поясі (перший момент, локальна дата якого
 *   дорівнює дню: перехід на літній час опівночі зсуває його на 01:00) і початок наступного дня.
 * - «Останні N днів» — сьогодні й N − 1 днів перед ним; «наступні N днів» — сьогодні й N − 1 після.
 * - Тиждень — з понеділка до неділі; квартал — січень–березень і т. д.
 */

/** Годинник умови: «зараз» і пояс, у якому рахуються дні (пояс глядача або календаря компанії). */
export interface FilterClock {
  readonly now: Date;
  readonly timeZone: string;
}

/** Пояс календаря компанії за замовчуванням платформи (`settings.company.timeZone`). */
export const DEFAULT_FILTER_TIME_ZONE = 'Europe/Kyiv';

/** Токени дня (`on`, `before`, `after`); `days_ago` / `days_ahead` — з `n`. */
export const DAY_TOKENS = ['today', 'yesterday', 'tomorrow', 'days_ago', 'days_ahead'] as const;
/** Токени діапазону днів (`within`); `last_n_days` / `next_n_days` — з `n`. */
export const RANGE_TOKENS = [
  'this_week',
  'last_week',
  'next_week',
  'this_month',
  'last_month',
  'next_month',
  'this_quarter',
  'last_quarter',
  'next_quarter',
  'this_year',
  'last_year',
  'next_year',
  'last_n_days',
  'next_n_days',
] as const;
/** Токен моменту (`lt`, `lte`, `gt`, `gte` на полі «дата-час»). */
export const MOMENT_TOKEN = 'now';

const DAY: ReadonlySet<string> = new Set(DAY_TOKENS);
const RANGE: ReadonlySet<string> = new Set(RANGE_TOKENS);

/**
 * Межі `n` токенів дат: зсув днів — 0…3650 («0 днів тому» — сьогодні), кількість днів діапазону —
 * 1…3650. Десять років — більше за будь-яке подання, але дата лишається в межах 0001–9999.
 */
export const DATE_TOKEN_N = { maxDays: 3650 } as const;
const WITH_N: Readonly<Record<string, number>> = {
  days_ago: 0,
  days_ahead: 0,
  last_n_days: 1,
  next_n_days: 1,
};

/** Токен дат відомий і має `n` лише там, де треба, у його межах. */
function dateTokenShape(token: FilterToken): boolean {
  const min = WITH_N[token.token];
  if (min === undefined) return token.n === undefined;
  return (
    token.n !== undefined &&
    Number.isSafeInteger(token.n) &&
    token.n >= min &&
    token.n <= DATE_TOKEN_N.maxDays
  );
}

export function isDayToken(token: FilterToken): boolean {
  return DAY.has(token.token) && dateTokenShape(token);
}
export function isRangeToken(token: FilterToken): boolean {
  return RANGE.has(token.token) && dateTokenShape(token);
}
export function isMomentToken(token: FilterToken): boolean {
  return token.token === MOMENT_TOKEN && token.n === undefined;
}
/** Назва належить токенам дат (форма `n` може бути й хибною). */
export function isDateTokenName(name: string): boolean {
  return DAY.has(name) || RANGE.has(name) || name === MOMENT_TOKEN;
}

// ---------------------------------------------------------------- календар

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Пояс, який знає ICU; невідомий — UTC (як `localDate` значень за замовчуванням). */
export function knownTimeZone(timeZone: string): string {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch {
    return 'UTC';
  }
}

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      era: 'short',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function localParts(ms: number, timeZone: string): LocalParts {
  const out: Record<string, number> = {};
  let bc = false;
  for (const part of formatter(timeZone).formatToParts(new Date(ms))) {
    if (part.type === 'era') bc = part.value.startsWith('B');
    else if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  const year = bc ? 1 - (out.year ?? 0) : (out.year ?? 0);
  return {
    year,
    month: out.month ?? 1,
    day: out.day ?? 1,
    hour: out.hour ?? 0,
    minute: out.minute ?? 0,
    second: out.second ?? 0,
  };
}

/** Мілісекунди UTC для «стінного» часу (рік 1…99 без зсуву на 1900, як у `Date.UTC`). */
function utcMs(p: LocalParts): number {
  const d = new Date(0);
  d.setUTCFullYear(p.year, p.month - 1, p.day);
  d.setUTCHours(p.hour, p.minute, p.second, 0);
  return d.getTime();
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

function dayText(p: Pick<LocalParts, 'year' | 'month' | 'day'>): string {
  return `${pad(p.year, 4)}-${pad(p.month, 2)}-${pad(p.day, 2)}`;
}

/** Локальна дата моменту `ms` у поясі. */
export function localDayOf(ms: number, timeZone: string): string {
  return dayText(localParts(ms, timeZone));
}

/** Зсув поясу в мілісекундах (локальний «стінний» час − UTC) у момент `ms`, до секунди. */
function offsetAt(ms: number, timeZone: string): number {
  const whole = Math.floor(ms / 1000) * 1000;
  return utcMs(localParts(whole, timeZone)) - whole;
}

function parseDay(day: string): { year: number; month: number; day: number } {
  return {
    year: Number(day.slice(0, 4)),
    month: Number(day.slice(5, 7)),
    day: Number(day.slice(8, 10)),
  };
}

/** День + N днів (календар, без поясу). */
export function addDays(day: string, n: number): string {
  const ms = utcMs({ ...parseDay(day), hour: 0, minute: 0, second: 0 }) + n * 86_400_000;
  const d = new Date(ms);
  return dayText({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() });
}

/**
 * Момент початку дня в поясі, мілісекунди UTC: найраніший серед кандидатів «північ мінус зсув»
 * (зсув — до, у й після півночі), локальна дата якого — цей день. Так перехід часу опівночі дає
 * перший наявний момент дня (01:00), а повтор години — перше 00:00.
 */
export function dayStartMs(day: string, timeZone: string): number {
  const zone = knownTimeZone(timeZone);
  const midnight = utcMs({ ...parseDay(day), hour: 0, minute: 0, second: 0 });
  let best: number | undefined;
  for (const probe of [midnight - 86_400_000, midnight, midnight + 86_400_000]) {
    const candidate = midnight - offsetAt(probe, zone);
    if (localDayOf(candidate, zone) !== day) continue;
    if (best === undefined || candidate < best) best = candidate;
  }
  // День без жодного моменту (пояс перестрибнув добу, Pacific/Apia 2011-12-30): з наступного дня.
  return best ?? dayStartMs(addDays(day, 1), zone);
}

/** Перший і останній день календаря мови умов (дата — з 0001, рік чотирма цифрами). */
export const FIRST_DAY = '0001-01-01';
export const LAST_DAY = '9999-12-31';

/**
 * Межі діапазону днів на полі «дата-час», мілісекунди UTC: початок першого дня й початок дня після
 * останнього в поясі. На краях календаря — `-Infinity` / `Infinity` (у SQL `-infinity` /
 * `infinity`): початок 0001-01-01 у поясі зі сходу від UTC припадає на рік 0000, якого PostgreSQL
 * не приймає, а дня після 9999-12-31 у календарі немає. DAL і оцінювач беруть межі звідси.
 */
export function dayRangeBounds(range: DayRange, timeZone: string): { start: number; end: number } {
  return {
    start: range.from <= FIRST_DAY ? -Infinity : dayStartMs(range.from, timeZone),
    end: range.to >= LAST_DAY ? Infinity : dayStartMs(addDays(range.to, 1), timeZone),
  };
}

/** Сьогодні за годинником. */
export function todayOf(clock: FilterClock): string {
  return localDayOf(clock.now.getTime(), knownTimeZone(clock.timeZone));
}

/** День за токеном дня; `undefined` — токен не дня або хибне `n`. */
export function dayOfToken(token: FilterToken, clock: FilterClock): string | undefined {
  if (!isDayToken(token)) return undefined;
  const today = todayOf(clock);
  switch (token.token) {
    case 'today':
      return today;
    case 'yesterday':
      return addDays(today, -1);
    case 'tomorrow':
      return addDays(today, 1);
    case 'days_ago':
      return addDays(today, -(token.n as number));
    default:
      return addDays(today, token.n as number);
  }
}

export interface DayRange {
  /** Перший день, включно. */
  readonly from: string;
  /** Останній день, включно. */
  readonly to: string;
}

/** Понеділок тижня дня. */
function weekStart(day: string): string {
  const ms = utcMs({ ...parseDay(day), hour: 0, minute: 0, second: 0 });
  const weekday = (new Date(ms).getUTCDay() + 6) % 7;
  return addDays(day, -weekday);
}

/** Перший день місяця зі зсувом на `months` (0 — цей місяць). */
function monthStart(day: string, months: number): string {
  const { year, month } = parseDay(day);
  const index = year * 12 + (month - 1) + months;
  return dayText({ year: Math.floor(index / 12), month: (index % 12) + 1, day: 1 });
}

function monthsRange(start: string, months: number): DayRange {
  return { from: start, to: addDays(monthStart(start, months), -1) };
}

/** Діапазон днів за токеном діапазону; `undefined` — токен не діапазону або хибне `n`. */
export function rangeOfToken(token: FilterToken, clock: FilterClock): DayRange | undefined {
  if (!isRangeToken(token)) return undefined;
  const today = todayOf(clock);
  const { month } = parseDay(today);
  const quarter = monthStart(today, -((month - 1) % 3));
  const year = monthStart(today, -(month - 1));
  const week = (shift: number): DayRange => {
    const from = addDays(weekStart(today), shift * 7);
    return { from, to: addDays(from, 6) };
  };
  switch (token.token) {
    case 'this_week':
      return week(0);
    case 'last_week':
      return week(-1);
    case 'next_week':
      return week(1);
    case 'this_month':
      return monthsRange(monthStart(today, 0), 1);
    case 'last_month':
      return monthsRange(monthStart(today, -1), 1);
    case 'next_month':
      return monthsRange(monthStart(today, 1), 1);
    case 'this_quarter':
      return monthsRange(quarter, 3);
    case 'last_quarter':
      return monthsRange(monthStart(quarter, -3), 3);
    case 'next_quarter':
      return monthsRange(monthStart(quarter, 3), 3);
    case 'this_year':
      return monthsRange(year, 12);
    case 'last_year':
      return monthsRange(monthStart(year, -12), 12);
    case 'next_year':
      return monthsRange(monthStart(year, 12), 12);
    case 'last_n_days':
      return { from: addDays(today, 1 - (token.n as number)), to: today };
    default:
      return { from: today, to: addDays(today, (token.n as number) - 1) };
  }
}

const CLOCK_OPERATORS: ReadonlySet<string> = new Set(['on', 'before', 'after', 'within']);

/**
 * Чи потрібен умові годинник: оператор дня чи діапазону або токен дат. Викликач лише тоді шукає
 * пояс глядача (запит до IAM); дерево поза граматикою — ні (його відхилить DAL). Умову профілю
 * `requirement` (крок, `related`) розбирати з `{ profile: 'requirement' }`.
 */
export function filterUsesClock(input: unknown, options: ParseFilterOptions = {}): boolean {
  const parsed = parseFilterTree(input, undefined, options);
  if (!parsed.ok) return false;
  return parsed.conditions.some(
    (condition) =>
      CLOCK_OPERATORS.has(condition.op) ||
      valuesOf(condition).some((item) => isFilterToken(item) && isDateTokenName(item.token)),
  );
}
